import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Chip,
  ClickAwayListener,
  Popover,
  Grid,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SortIcon from "@mui/icons-material/Sort";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { PLAYER_CARD_COLORS } from "../../../utils/colorConstants";
import api from "../../../utils/api";

// Import AtlasKit components
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

// Import components
import ProspectCard from "./ProspectCard";

// Draggable recruit card component
const DraggableRecruitCard = ({
  recruit,
  index,
  columnId,
  onActionMenuOpen,
  onRecruitMove,
  onViewProfile,
}) => {
  const theme = useTheme();
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const element = dragRef.current;
    if (!element) return;

    // Make the card draggable
    const draggableCard = draggable({
      element,
      getInitialData: () => ({
        type: "recruit-card",
        recruitId: recruit.id,
        sourceColumnId: columnId,
        sourceIndex: index,
      }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });

    // Make the card a drop target for other cards
    const dropTarget = dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        return source.data.type === "recruit-card";
      },
      getData: () => ({
        targetIndex: index,
        targetColumnId: columnId,
      }),
      onDragEnter: () => {
        element.style.transform = "scale(1.02)";
        element.style.boxShadow = theme.shadows[3];
      },
      onDragLeave: () => {
        element.style.transform = "";
        element.style.boxShadow = "";
      },
      onDrop: ({ source }) => {
        element.style.transform = "";
        element.style.boxShadow = "";

        const { recruitId, sourceColumnId, sourceIndex } = source.data;

        // Don't do anything if dropping on itself
        if (sourceColumnId === columnId && sourceIndex === index) {
          return;
        }

        // Calculate the destination index
        let destinationIndex = index;

        // If moving within the same column and moving down, we need to adjust the index
        if (sourceColumnId === columnId && sourceIndex < index) {
          destinationIndex -= 1;
        }

        onRecruitMove({
          recruitId,
          sourceColumnId,
          sourceIndex,
          destinationColumnId: columnId,
          destinationIndex: destinationIndex,
        });
      },
    });

    return combine(draggableCard, dropTarget);
  }, [recruit.id, columnId, index, theme.shadows, onRecruitMove]);

  // Handle card actions
  const handleAddNote = () => {
    onActionMenuOpen(null, recruit, "note");
  };

  const handleSetColor = () => {
    onActionMenuOpen(null, recruit, "color");
  };

  const handleViewProfile = () => {
    if (!isDragging) {
      onViewProfile(recruit.id);
    }
  };

  return (
    <div
      ref={dragRef}
      style={{
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? "rotate(2deg)" : "none",
        transition: "all 0.2s ease",
        marginBottom: "8px",
      }}
    >
      <ProspectCard
        prospect={recruit}
        onAddNote={handleAddNote}
        onSetColor={handleSetColor}
        onViewProfile={handleViewProfile}
        isDragging={isDragging}
      />
    </div>
  );
};

// Predefined color options for columns
const columnColorOptions = [
  "#2196f3", // Blue
  "#4caf50", // Green
  "#f44336", // Red
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#795548", // Brown
  "#607d8b", // Blue Grey
  "#009688", // Teal
  "#673ab7", // Deep Purple
  "#3f51b5", // Indigo
];

// Droppable column component
const DroppableColumn = ({
  column,
  onRecruitMove,
  onActionMenuOpen,
  onColumnUpdate,
  onViewProfile,
}) => {
  const dropRef = useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(column.title);

  // Column menu state
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);
  const [selectedColor, setSelectedColor] = useState(column.color || "#2196f3");

  // Sort state
  const [sortOrder, setSortOrder] = useState(column.sortOrder || "none"); // "newest", "oldest", "none"

  // Keep selectedColor in sync with column.color
  useEffect(() => {
    setSelectedColor(column.color || "#2196f3");
  }, [column.color]);

  // Keep sortOrder in sync with column.sortOrder - only if it's a valid value
  useEffect(() => {
    console.log(
      "Column received sortOrder:",
      column.sortOrder,
      "for column ID:",
      column.id
    );
    // Only update if we receive a valid sortOrder value (don't reset to "none" when undefined)
    if (column.sortOrder !== undefined) {
      setSortOrder(column.sortOrder);
    }
  }, [column.sortOrder, column.id]);

  // Handle column menu open
  const handleColumnMenuOpen = (event) => {
    console.log("Column menu opened for:", column.title);
    setColumnMenuAnchorEl(event.currentTarget);
  };

  // Handle column menu close
  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  // Handle color picker open
  const handleColorPickerOpen = (event) => {
    setColorPickerAnchorEl(event.currentTarget);
    handleColumnMenuClose();
  };

  // Handle color picker close
  const handleColorPickerClose = () => {
    setColorPickerAnchorEl(null);
  };

  // Handle color selection
  const handleColorSelect = (color) => {
    setSelectedColor(color);
    onColumnUpdate(column.id, { color });
    handleColorPickerClose();
  };

  // Handle sort change
  const handleSortChange = (newSortOrder) => {
    console.log(
      "Sort change requested:",
      newSortOrder,
      "for column:",
      column.title
    );
    console.log("Current sortOrder before change:", sortOrder);

    // Update local state immediately
    setSortOrder(newSortOrder);

    // Update parent
    onColumnUpdate(column.id, { sortOrder: newSortOrder });

    // Close menu
    handleColumnMenuClose();

    console.log("handleSortChange completed");
  };

  // Specific handlers for each sort option
  const handleSortNewest = () => {
    console.log("handleSortNewest called");
    handleSortChange("newest");
  };

  const handleSortOldest = () => {
    console.log("handleSortOldest called");
    handleSortChange("oldest");
  };

  const handleSortNone = () => {
    console.log("handleSortNone called");
    handleSortChange("none");
  };

  // Sort recruits based on current sort order
  const getSortedRecruits = () => {
    if (!column.recruits) return [];

    const recruitsWithSort = [...column.recruits];

    if (sortOrder === "newest") {
      return recruitsWithSort.sort((a, b) => {
        const aTime = a.addedToColumnAt || a.addedAt || a.createdAt || 0;
        const bTime = b.addedToColumnAt || b.addedAt || b.createdAt || 0;
        return new Date(bTime) - new Date(aTime); // Newest first
      });
    } else if (sortOrder === "oldest") {
      return recruitsWithSort.sort((a, b) => {
        const aTime = a.addedToColumnAt || a.addedAt || a.createdAt || 0;
        const bTime = b.addedToColumnAt || b.addedAt || b.createdAt || 0;
        return new Date(aTime) - new Date(bTime); // Oldest first
      });
    }

    return recruitsWithSort; // Default order
  };

  useEffect(() => {
    const element = dropRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === "recruit-card",
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source }) => {
        setIsDraggedOver(false);
        const { recruitId, sourceColumnId, sourceIndex } = source.data;

        // Calculate the destination index based on the position of existing cards
        let destinationIndex = column.recruits?.length || 0;

        // If dropping on the same column, we need to determine the position
        // based on the drop location relative to other cards
        if (sourceColumnId === column.id) {
          // For now, we'll just append to the end if it's the same column
          // The more precise positioning will be handled by the card-to-card drop
          destinationIndex = column.recruits?.length || 0;
        }

        onRecruitMove({
          recruitId,
          sourceColumnId,
          sourceIndex,
          destinationColumnId: column.id,
          destinationIndex: destinationIndex,
        });
      },
    });
  }, [column.id, column.recruits?.length, onRecruitMove]);

  return (
    <Box
      sx={{
        width: 300,
        mx: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2,
          mb: 1,
          borderRadius: "8px 8px 0 0",
          bgcolor: `${column.color}25`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderLeft: `4px solid ${column.color}`,
          borderTop: `1px solid ${column.color}`,
          borderRight: `1px solid ${column.color}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isEditing ? (
            <ClickAwayListener
              onClickAway={() => {
                if (editedTitle.trim() !== "") {
                  onColumnUpdate(column.id, { title: editedTitle });
                }
                setIsEditing(false);
              }}
            >
              <TextField
                autoFocus
                size="small"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && editedTitle.trim() !== "") {
                    onColumnUpdate(column.id, { title: editedTitle });
                    setIsEditing(false);
                  } else if (e.key === "Escape") {
                    setEditedTitle(column.title);
                    setIsEditing(false);
                  }
                }}
                sx={{ width: "120px" }}
              />
            </ClickAwayListener>
          ) : (
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                cursor: "default", // Remove pointer cursor
              }}
            >
              {column.title}
            </Typography>
          )}
          <Chip
            label={column.recruits?.length || 0}
            size="small"
            sx={{ ml: 1, bgcolor: column.color, color: "white" }}
          />
          {sortOrder !== "none" && (
            <Box sx={{ ml: 0.5, display: "flex", alignItems: "center" }}>
              {sortOrder === "newest" ? (
                <ArrowDownwardIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              ) : (
                <ArrowUpwardIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              )}
            </Box>
          )}
        </Box>
        <IconButton size="small" onClick={handleColumnMenuOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>

        {/* Column Menu */}
        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleColumnMenuClose}
          MenuListProps={{
            "aria-labelledby": "column-menu-button",
            dense: false,
          }}
        >
          <MenuItem
            onClick={() => {
              setIsEditing(true);
              setEditedTitle(column.title);
              handleColumnMenuClose();
            }}
          >
            Rename Column
          </MenuItem>
          <MenuItem onClick={handleColorPickerOpen}>Change Color</MenuItem>
          <Divider />
          <MenuItem onClick={handleSortNewest}>
            <ArrowDownwardIcon sx={{ mr: 1, fontSize: 16 }} />
            Sort by Newest First
            {sortOrder === "newest" && (
              <Box sx={{ ml: "auto", color: "primary.main" }}>✓</Box>
            )}
          </MenuItem>
          <MenuItem onClick={handleSortOldest}>
            <ArrowUpwardIcon sx={{ mr: 1, fontSize: 16 }} />
            Sort by Oldest First
            {sortOrder === "oldest" && (
              <Box sx={{ ml: "auto", color: "primary.main" }}>✓</Box>
            )}
          </MenuItem>
          <MenuItem onClick={handleSortNone}>
            <SortIcon sx={{ mr: 1, fontSize: 16 }} />
            Default Order
            {sortOrder === "none" && (
              <Box sx={{ ml: "auto", color: "primary.main" }}>✓</Box>
            )}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              handleColumnMenuClose();
              onColumnUpdate(column.id, { delete: true });
            }}
            sx={{ color: "error.main" }}
          >
            Delete Column
          </MenuItem>
        </Menu>
      </Box>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchorEl)}
        anchorEl={colorPickerAnchorEl}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, width: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select Column Color
          </Typography>
          <Grid container spacing={1}>
            {columnColorOptions.map((color) => (
              <Grid item key={color}>
                <Box
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: color,
                    borderRadius: "50%",
                    cursor: "pointer",
                    border:
                      color === selectedColor
                        ? "2px solid black"
                        : "2px solid transparent",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Column Content */}
      <Box
        ref={dropRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: isDraggedOver ? `${column.color}15` : `${column.color}05`,
          borderRadius: "0 0 8px 8px",
          p: 1,
          minHeight: 200,
          transition: "background-color 0.2s ease",
          border: isDraggedOver
            ? `2px dashed ${column.color}`
            : `1px solid ${column.color}30`,
        }}
      >
        {getSortedRecruits().map((recruit, index) => (
          <DraggableRecruitCard
            key={recruit.id || `recruit-${column.id}-${index}`}
            recruit={recruit}
            index={index}
            columnId={column.id}
            onActionMenuOpen={onActionMenuOpen}
            onRecruitMove={onRecruitMove}
            onViewProfile={onViewProfile}
          />
        ))}

        {/* Empty column placeholder */}
        {getSortedRecruits().length === 0 && (
          <Box
            sx={{
              height: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px dashed",
              borderColor: isDraggedOver ? column.color : `${column.color}50`,
              borderRadius: 2,
              p: 2,
              backgroundColor: isDraggedOver
                ? `${column.color}20`
                : `${column.color}10`,
              transition: "all 0.2s ease",
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              {isDraggedOver ? "Drop here" : "Drag recruits here"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const KanbanBoard = ({
  columns: initialColumns,
  onColumnsUpdate,
  onRecruitUpdate,
}) => {
  const navigate = useNavigate();

  // State for columns with recruits
  const [columns, setColumns] = useState(initialColumns || []);

  // Debounced update function to prevent excessive API calls
  const debouncedUpdate = useRef(null);

  const debouncedColumnsUpdate = useCallback(
    (updatedColumns) => {
      // Check if columns actually changed to prevent unnecessary updates
      const hasChanged =
        JSON.stringify(updatedColumns) !== JSON.stringify(columns);
      if (!hasChanged) return;

      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }

      debouncedUpdate.current = setTimeout(() => {
        if (onColumnsUpdate) {
          onColumnsUpdate(updatedColumns);
        }
      }, 300); // 300ms delay
    },
    [onColumnsUpdate, columns]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debouncedUpdate.current) {
        clearTimeout(debouncedUpdate.current);
      }
    };
  }, []);

  // Update local columns when initialColumns change (e.g., from parent)
  // Only update if we're not in the middle of a drag operation or local update
  const isLocalUpdate = useRef(false);

  useEffect(() => {
    // Skip update if this is a result of our own local update
    if (isLocalUpdate.current) {
      isLocalUpdate.current = false;
      return;
    }

    if (
      initialColumns &&
      JSON.stringify(initialColumns) !== JSON.stringify(columns)
    ) {
      setColumns(initialColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColumns]); // Intentionally excluding 'columns' to prevent feedback loop

  // State for add column dialog
  const [addColumnDialogOpen, setAddColumnDialogOpen] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState("");
  const [newColumnColor, setNewColumnColor] = useState("#2196f3"); // Default blue color

  // State for note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState("");
  const [currentRecruit, setCurrentRecruit] = useState(null);

  // State for color dialog
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#2196f3");

  // Update parent component when columns change - only when explicitly triggered
  // Removed automatic updates to prevent infinite loops

  // Handle individual recruit updates (color, notes)
  const handleRecruitUpdate = async (recruitId, updates) => {
    try {
      // Find the specific column and recruit to update
      let foundRecruit = null;
      let foundColumnId = null;

      for (const column of columns) {
        if (column.recruits) {
          const recruitIndex = column.recruits.findIndex(
            (r) => r.id === recruitId || r._id === recruitId
          );
          if (recruitIndex !== -1) {
            foundRecruit = column.recruits[recruitIndex];
            foundColumnId = column.id;
            break;
          }
        }
      }

      if (!foundRecruit) {
        console.error("Recruit not found:", recruitId);
        return;
      }

      // Update only the specific recruit - preserve the original structure
      const updatedColumns = columns.map((column) => {
        if (column.id !== foundColumnId) return column;

        return {
          ...column,
          recruits: column.recruits.map((recruit) => {
            if (recruit.id === recruitId || recruit._id === recruitId) {
              // Only update the specific fields, don't transform the structure
              return { ...recruit, ...updates };
            }
            return recruit;
          }),
        };
      });

      // Update local state immediately for responsive UI
      isLocalUpdate.current = true;
      setColumns(updatedColumns);

      // IMPORTANT: Call the backend API to persist the changes
      if (onRecruitUpdate) {
        await onRecruitUpdate(recruitId, updates);
      }

      // DO NOT call debouncedColumnsUpdate here - individual recruit updates
      // should not trigger board-wide updates
    } catch (error) {
      console.error("Error updating recruit:", error);
    }
  };

  // Handle column update (title, color, delete)
  const handleColumnUpdate = (columnId, updates) => {
    console.log("handleColumnUpdate called with:", { columnId, updates });

    // Handle column deletion
    if (updates.delete) {
      const updatedColumns = columns.filter((col) => col.id !== columnId);
      isLocalUpdate.current = true;
      setColumns(updatedColumns);

      // Only call onColumnsUpdate after column updates
      debouncedColumnsUpdate(updatedColumns);
      return;
    }

    // Handle other updates (title, color, sortOrder)
    const updatedColumns = columns.map((col) =>
      col.id === columnId ? { ...col, ...updates } : col
    );
    console.log("Updated columns:", updatedColumns);
    isLocalUpdate.current = true;
    setColumns(updatedColumns);

    // Only call onColumnsUpdate after column updates
    debouncedColumnsUpdate(updatedColumns);
  };

  // Handle opening add column dialog
  const handleAddColumnDialogOpen = () => {
    setNewColumnTitle("");
    setNewColumnColor("#2196f3");
    setAddColumnDialogOpen(true);
  };

  // Handle closing add column dialog
  const handleAddColumnDialogClose = () => {
    setAddColumnDialogOpen(false);
  };

  // Handle adding a new column
  const handleAddColumn = () => {
    if (newColumnTitle.trim() === "") return;

    const newColumn = {
      id: `column-${Date.now()}`, // Generate unique ID
      title: newColumnTitle,
      color: newColumnColor,
      recruits: [],
    };

    const updatedColumns = [...columns, newColumn];
    isLocalUpdate.current = true;
    setColumns(updatedColumns);

    // Only call onColumnsUpdate after adding a new column
    debouncedColumnsUpdate(updatedColumns);

    handleAddColumnDialogClose();
  };

  // State for action menu
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);

  // Handle recruit move between or within columns
  const handleRecruitMove = ({
    recruitId,
    sourceColumnId,
    sourceIndex,
    destinationColumnId,
    destinationIndex,
  }) => {
    const newColumns = [...columns];

    // Find source and destination columns
    const sourceColumnIndex = newColumns.findIndex(
      (col) => col.id === sourceColumnId
    );
    const destColumnIndex = newColumns.findIndex(
      (col) => col.id === destinationColumnId
    );

    if (sourceColumnIndex === -1 || destColumnIndex === -1) {
      return;
    }

    // Special handling for same column moves to prevent duplicates
    if (sourceColumnId === destinationColumnId) {
      // We're moving within the same column
      const columnCopy = { ...newColumns[sourceColumnIndex] };
      columnCopy.recruits = [...(columnCopy.recruits || [])];

      // Remove the item from its original position
      const [movedRecruit] = columnCopy.recruits.splice(sourceIndex, 1);

      // Insert it at the new position
      columnCopy.recruits.splice(destinationIndex, 0, movedRecruit);

      // Update the column
      newColumns[sourceColumnIndex] = columnCopy;
    } else {
      // Moving between different columns
      const sourceColumn = { ...newColumns[sourceColumnIndex] };
      const destColumn = { ...newColumns[destColumnIndex] };

      // Create new recruits arrays
      sourceColumn.recruits = [...(sourceColumn.recruits || [])];
      destColumn.recruits = [...(destColumn.recruits || [])];

      // Remove recruit from source - preserve the original structure
      const [removedRecruit] = sourceColumn.recruits.splice(sourceIndex, 1);

      // Ensure the recruit maintains its original structure with prospect object
      // Don't transform the data structure - keep it as is
      // Add timestamp for when this recruit was added to the new column
      const recruitToMove = {
        ...removedRecruit,
        addedAt: new Date().toISOString(),
      };

      // Add recruit to destination
      destColumn.recruits.splice(destinationIndex, 0, recruitToMove);

      // Update the columns
      newColumns[sourceColumnIndex] = sourceColumn;
      newColumns[destColumnIndex] = destColumn;
    }

    // Mark this as a local update to prevent feedback loop
    isLocalUpdate.current = true;
    setColumns(newColumns);

    // Only call onColumnsUpdate after drag operations to prevent excessive API calls
    debouncedColumnsUpdate(newColumns);
  };

  // Handle action menu open
  const handleActionMenuOpen = (event, recruit, action = null) => {
    // Only set the anchor element if we have a valid event
    if (event) {
      setActionMenuAnchorEl(event.currentTarget);
    }
    setCurrentRecruit(recruit);

    // Handle direct actions
    if (action === "note") {
      setCurrentNote(recruit.notes || "");
      setNoteDialogOpen(true);
      return;
    }

    if (action === "color") {
      setSelectedColor(recruit.color || "#2196f3");
      setColorDialogOpen(true);
      return;
    }
  };

  // Handle action menu close
  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  // Handle opening the note dialog
  const handleOpenNoteDialog = () => {
    setNoteDialogOpen(true);
    setCurrentNote(currentRecruit?.notes || "");
    handleActionMenuClose();
  };

  // Handle closing the note dialog
  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
  };

  // Handle opening the color dialog
  const handleOpenColorDialog = () => {
    setColorDialogOpen(true);
    setSelectedColor(currentRecruit?.color || "#2196f3");
    handleActionMenuClose();
  };

  // Handle closing the color dialog
  const handleCloseColorDialog = () => {
    setColorDialogOpen(false);
  };

  // Handle viewing player profile from menu
  const handleViewProfile = () => {
    if (!currentRecruit) return;
    navigate(`/prospects/profile/${currentRecruit.id}`);
    handleActionMenuClose();
  };

  // Handle viewing player profile from card click
  const handleViewProfileById = (recruitId) => {
    navigate(`/prospects/profile/${recruitId}`);
  };

  // Handle saving the note
  const handleSaveNote = async () => {
    if (!currentRecruit) return;

    try {
      // Add note to the prospect entity, not the board
      const prospectId = currentRecruit.id || currentRecruit._id;
      if (prospectId) {
        await api.prospectNotes.create(prospectId, {
          text: currentNote,
          category: "general",
          source: "main",
          priority: "medium",
        });

        // Trigger refresh of the prospect card to show the new note
        // by updating the noteRefreshTrigger field
        const refreshTrigger = Date.now();
        await handleRecruitUpdate(currentRecruit.id, {
          noteRefreshTrigger: refreshTrigger,
        });

        handleCloseNoteDialog();
      }
    } catch (error) {
      console.error("Error adding note to prospect:", error);
      // You could show an error message to the user here
    }
  };

  // Handle saving the color
  const handleSaveColor = () => {
    if (!currentRecruit) return;

    // Update the specific recruit with the new color
    handleRecruitUpdate(currentRecruit.id, { color: selectedColor });
    handleCloseColorDialog();
  };

  return (
    <Box sx={{ overflowX: "auto", pb: 2 }}>
      <Box
        sx={{
          display: "flex",
          minWidth: columns.length * 300,
          maxWidth: "100%",
          height: "calc(100vh - 400px)",
          minHeight: 400,
        }}
      >
        {columns.map((column) => (
          <DroppableColumn
            key={column.id}
            column={column}
            onRecruitMove={handleRecruitMove}
            onActionMenuOpen={handleActionMenuOpen}
            onColumnUpdate={handleColumnUpdate}
            onViewProfile={handleViewProfileById}
          />
        ))}

        {/* Add Column Button */}
        <Box
          sx={{
            width: 280,
            minWidth: 280,
            mx: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddColumnDialogOpen}
            sx={{
              borderStyle: "dashed",
              borderWidth: 2,
              height: 50,
              width: "100%",
            }}
          >
            Add Column
          </Button>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={handleViewProfile}>View Profile</MenuItem>
        <MenuItem onClick={handleOpenNoteDialog}>Add Note</MenuItem>
        <MenuItem onClick={handleOpenColorDialog}>Set Color</MenuItem>
      </Menu>

      {/* Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={handleCloseNoteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {currentRecruit
            ? `Add Note for ${currentRecruit.firstName} ${currentRecruit.lastName}`
            : "Add Note"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Notes"
            value={currentNote}
            onChange={(e) => setCurrentNote(e.target.value)}
            variant="outlined"
            placeholder="Enter your notes here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoteDialog}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained" color="primary">
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Dialog */}
      <Dialog open={colorDialogOpen} onClose={handleCloseColorDialog}>
        <DialogTitle>Set Card Color</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 1,
              mb: 2,
            }}
          >
            {PLAYER_CARD_COLORS.map((color) => (
              <Box
                key={color.hex}
                onClick={() => setSelectedColor(color.hex)}
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: color.hex,
                  borderRadius: 1,
                  cursor: "pointer",
                  border:
                    selectedColor === color.hex
                      ? "3px solid black"
                      : "1px solid #ccc",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
                title={color.name}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseColorDialog}>Cancel</Button>
          <Button onClick={handleSaveColor} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Column Dialog */}
      <Dialog open={addColumnDialogOpen} onClose={handleAddColumnDialogClose}>
        <DialogTitle>Add New Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="column-title"
            label="Column Title"
            type="text"
            fullWidth
            value={newColumnTitle}
            onChange={(e) => setNewColumnTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select Column Color
          </Typography>
          <Grid container spacing={1}>
            {columnColorOptions.map((color) => (
              <Grid item key={color}>
                <Box
                  onClick={() => setNewColumnColor(color)}
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: color,
                    borderRadius: "50%",
                    cursor: "pointer",
                    border:
                      color === newColumnColor
                        ? "2px solid black"
                        : "2px solid transparent",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddColumnDialogClose}>Cancel</Button>
          <Button
            onClick={handleAddColumn}
            variant="contained"
            disabled={newColumnTitle.trim() === ""}
          >
            Add Column
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KanbanBoard;
